import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { B } from '../constants';
import { destinations as destinationsAPI } from '../api';
import { Sheet, fmtKm, fmtVisit } from './PlaceSheet';
import { ImgOrFallback } from './FallbackImg';

// Loaded on demand: Leaflet is only needed once a sheet is opened.
const MapView = lazy(() => import('./MapView'));

const SERIF = "'Playfair Display',serif";

// Every verified place we hold for a city's metro, filterable by category, on a map,
// with each one's distance from the city centre. Tapping a place opens its sheet.
export default function VerifiedPlacesSheet({ cityId, cityName, categoryLabel, onOpenPlace, onAddToTrip, onClose }) {
  const [places, setPlaces] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cat, setCat] = useState('all');
  const [sortBy, setSortBy] = useState('popular');

  const [focusId, setFocusId] = useState(null);
  const rowRefs = useRef({});

  const runRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    runRef.current += 1;
    destinationsAPI.palette({ cityId, page: 0 })
      .then((r) => { if (!cancelled) { setPlaces(r.places || []); setHasMore(!!r.hasMore); setPage(0); } })
      .catch(() => { if (!cancelled) setPlaces([]); });
    return () => { cancelled = true; runRef.current += 1; };
  }, [cityId]);

  // Fetch every remaining server page; stops early if the sheet closes or the city changes.
  const loadAll = async () => {
    const run = runRef.current;
    setLoadingMore(true);
    try {
      for (let next = page + 1; ; next += 1) {
        const r = await destinationsAPI.palette({ cityId, page: next });
        if (runRef.current !== run) return;
        setPlaces((cur) => {
          const ids = new Set(cur.map((p) => p.id));
          return [...cur, ...(r.places || []).filter((p) => !ids.has(p.id))];
        });
        setPage(next);
        if (!r.hasMore) { setHasMore(false); return; }
      }
    } catch {
      if (runRef.current === run) setHasMore(false);
    } finally {
      if (runRef.current === run) setLoadingMore(false);
    }
  };

  const categories = useMemo(() => {
    const counts = new Map();
    for (const p of places || []) counts.set(p.category, (counts.get(p.category) || 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [places]);

  const shown = useMemo(() => {
    const list = (places || []).filter((p) => cat === 'all' || p.category === cat);
    return sortBy === 'nearest' ? list.slice().sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity)) : list;
  }, [places, cat, sortBy]);

  // Tapping a map pin highlights its row and scrolls the list to it.
  const focusPlace = (id) => {
    setFocusId(id);
    const row = rowRefs.current[id];
    if (row && row.scrollIntoView) row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const pill = (on) => ({
    padding: '6px 12px', borderRadius: 999, border: `1px solid ${on ? B.forest : '#e2ddd3'}`,
    background: on ? B.forest : '#fff', color: on ? '#fff' : B.night, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
  });

  return (
    <Sheet title={`Verified places in ${cityName}`} onClose={onClose} maxWidth={760}>
      <div style={{ padding: '18px 20px 24px' }}>
        <h3 style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700, margin: '0 0 4px', color: B.night, paddingRight: 44 }}>Verified places in {cityName}</h3>
        <p style={{ fontSize: 13, color: B.muted, margin: '0 0 14px' }}>
          {places == null ? 'Loading places…' : `${shown.length} place${shown.length === 1 ? '' : 's'}${hasMore ? '+' : ''} · distances are straight-line from the ${cityName} city centre`}
        </p>
        {categories.length > 1 && (
          <div className="gb-chips" style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 8, scrollbarWidth: 'none' }}>
            <style>{'.gb-chips::-webkit-scrollbar { display: none; }'}</style>
            <button type="button" style={pill(cat === 'all')} onClick={() => setCat('all')}>All</button>
            {categories.map(([c, n]) => (
              <button key={c} type="button" style={pill(cat === c)} onClick={() => setCat(c)}>{categoryLabel(c)} ({n})</button>
            ))}
          </div>
        )}
        {shown.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Suspense fallback={null}><MapView height={260} selectedId={focusId} onActivityHover={(m) => focusPlace(m.id)}
              locations={shown.slice(0, 150).map((p) => ({
                id: p.id, lat: p.lat, lng: p.lng, name: p.name, pin: true, imageUrl: p.imageUrl,
                description: [categoryLabel(p.category), p.distanceKm != null ? `${fmtKm(p.distanceKm)} from centre` : null].filter(Boolean).join(' · '),
                actions: [
                  onAddToTrip && { label: '+ Add to trip', primary: true, ariaLabel: `Add ${p.name} to a trip`, onClick: () => onAddToTrip(p) },
                  { label: 'Details', ariaLabel: `Details for ${p.name}`, onClick: () => onOpenPlace(p) },
                ],
              }))} /></Suspense>
          </div>
        )}
        {shown.length > 1 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <button type="button" style={pill(sortBy === 'popular')} onClick={() => setSortBy('popular')}>Most popular</button>
            <button type="button" style={pill(sortBy === 'nearest')} onClick={() => setSortBy('nearest')}>Closest to centre</button>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.map((p) => (
            <div key={p.id} ref={(el) => { if (el) rowRefs.current[p.id] = el; else delete rowRefs.current[p.id]; }}
              data-focused={focusId === p.id ? 'true' : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: focusId === p.id ? '#f1f8f4' : '#fff', border: `1px solid ${focusId === p.id ? B.forest : '#ece7de'}`, borderRadius: 14, paddingRight: onAddToTrip ? 10 : 0 }}>
            <button type="button" onClick={() => onOpenPlace(p)}
              style={{ display: 'flex', gap: 12, alignItems: 'center', flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', borderRadius: 14, padding: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
              <span style={{ width: 52, height: 52, borderRadius: 10, flex: '0 0 auto', background: '#eef5f1', overflow: 'hidden', display: 'grid', placeItems: 'center', fontSize: 20 }} aria-hidden="true">
                <ImgOrFallback src={p.imageUrl} fallback="📍" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 14, color: B.night }}>{p.name}</span>
                {p.description && <span style={{ display: 'block', fontSize: 12, color: '#3b4450', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.description}</span>}
                <span style={{ display: 'block', fontSize: 11.5, color: B.muted, marginTop: 2 }}>
                  {[categoryLabel(p.category), p.durationMin ? `⏱ ${fmtVisit(p.durationMin)} visit` : null, p.distanceKm != null ? `${fmtKm(p.distanceKm)} from centre` : null].filter(Boolean).join(' · ')}
                </span>
              </span>
              {!onAddToTrip && <span aria-hidden="true" style={{ color: B.forest, fontWeight: 700 }}>›</span>}
            </button>
            {onAddToTrip && (
              <button type="button" onClick={() => onAddToTrip(p)} aria-label={`Add ${p.name} to a trip`}
                style={{ ...pill(false), flex: '0 0 auto', color: B.forest, borderColor: B.forest, padding: '6px 10px' }}>+ Add</button>
            )}
            </div>
          ))}
        </div>
        {hasMore && (
          <button type="button" onClick={loadAll} disabled={loadingMore} style={{ ...pill(false), width: '100%', marginTop: 12, padding: 10 }}>
            {loadingMore ? `Loading places… (${places.length} loaded)` : 'Show all places'}
          </button>
        )}
      </div>
    </Sheet>
  );
}
